import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRightLeft, Check } from 'lucide-react';
import { NovaTroca, type NovaTrocaHandle, type NovaTrocaEstado, type TipoTroca } from './NovaTroca';
import { HistoricoTrocas } from './HistoricoTrocas';
import { CreditosLista } from './CreditosLista';
import { formatMoeda } from '@fiodecobre/shared';

type Aba = 'nova' | 'historico' | 'creditos';

const ABAS: { id: Aba; label: string }[] = [
  { id: 'nova', label: 'Nova Troca' },
  { id: 'historico', label: 'Historico' },
  { id: 'creditos', label: 'Creditos da Loja' },
];

export function TrocasHome() {
  const [params, setParams] = useSearchParams();
  const editarParam = params.get('editar');
  const editarId = editarParam ? Number(editarParam) : null;
  const abaUrl = (params.get('aba') as Aba) || 'nova';
  const [aba, setAba] = useState<Aba>(editarId ? 'nova' : (ABAS.some((a) => a.id === abaUrl) ? abaUrl : 'nova'));

  // Ref + estado pra controlar os botoes de acao do header (so quando aba=nova).
  const novaTrocaRef = useRef<NovaTrocaHandle | null>(null);
  const [estadoNova, setEstadoNova] = useState<NovaTrocaEstado>({
    bloqueio: 'Selecione o cliente', salvando: false,
    valorDevolvido: 0, valorLevado: 0, saldoCredito: 0,
    tipo: 'devolucao', podeTrocar: false,
  });

  useEffect(() => {
    if (abaUrl !== aba) setAba(ABAS.some((a) => a.id === abaUrl) ? abaUrl : 'nova');
  }, [abaUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  function trocarAba(novaAba: Aba) {
    setAba(novaAba);
    const next = new URLSearchParams(params);
    next.set('aba', novaAba);
    if (novaAba !== 'historico') next.delete('validacao');
    next.delete('editar'); // sair da aba sempre encerra o modo correcao
    setParams(next, { replace: true });
  }

  function irParaCorrigir(id: number) {
    setAba('nova');
    const next = new URLSearchParams(params);
    next.set('aba', 'nova');
    next.set('editar', String(id));
    next.delete('validacao');
    setParams(next, { replace: true });
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Uma linha so (pedido dele, 22/10/2026): sem wrap; quem cede em tela estreita e' o texto das opcoes. */}
      <div className="pagina-header" style={{ flexShrink: 0, flexWrap: 'nowrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', flexShrink: 0 }}>
          <h2 className="pagina-titulo">
            <ArrowRightLeft size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
            Trocas e Devolucoes
          </h2>
          <div className="abas" style={{ margin: 0 }}>
            {ABAS.map((a) => (
              <button
                key={a.id}
                type="button"
                className={`aba-btn ${aba === a.id ? 'aba-btn-ativo' : ''}`}
                onClick={() => trocarAba(a.id)}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
        {/* As tres opcoes subiram da faixa do cliente pro cabecalho (pedido dele, 22/10/2026: ganhar
            altura). Quem guarda o tipo segue sendo a NovaTroca; aqui so desenha e repassa. */}
        {aba === 'nova' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14, flex: '0 1 auto', minWidth: 0 }}>
            {([
              { t: 'devolucao', nome: 'Devolucao', desc: 'so devolve (gera credito)' },
              ...(estadoNova.podeTrocar ? [{ t: 'troca', nome: 'Troca', desc: 'devolve produtos e leva outros' }] : []),
              // A correcao de uma troca retornada nao vira compra (o PUT so aceita troca/devolucao).
              ...(editarId ? [] : [{ t: 'compra_credito', nome: 'Compra com Credito', desc: 'pagando so com credito' }]),
            ] as Array<{ t: TipoTroca; nome: string; desc: string }>).map((o) => (
              <label key={o.t} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="tipo-troca"
                  checked={estadoNova.tipo === o.t}
                  onChange={() => novaTrocaRef.current?.escolherTipo(o.t)}
                />
                <span><strong>{o.nome}</strong> — {o.desc}</span>
              </label>
            ))}
          </div>
        )}
        {aba === 'nova' && (
          <div className="pagina-acoes" style={{ flexShrink: 0, flexWrap: 'nowrap' }}>
            <button
              type="button"
              className="btn-secundario"
              onClick={() => novaTrocaRef.current?.limpar()}
              disabled={estadoNova.salvando}
            >
              Limpar
            </button>
            <button
              type="button"
              className="btn-primario"
              onClick={() => novaTrocaRef.current?.finalizar()}
              disabled={!!estadoNova.bloqueio || estadoNova.salvando}
              title={estadoNova.bloqueio || ''}
            >
              <Check size={14} style={{ display: 'inline', marginRight: 4 }} />
              {estadoNova.salvando ? 'Salvando...' : (editarId ? 'Salvar Correcao' : estadoNova.compra ? 'Finalizar' : 'Finalizar Troca')}
            </button>
            {/* Compra com Credito: orcamento x credito no lugar de devolvido x levado */}
            {estadoNova.compra ? (
            <div style={{
              minWidth: 240,
              background: '#fff',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '8px 12px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 13, marginBottom: 2 }}>
                <strong style={{ color: 'var(--page-fg)' }}>Orcamento</strong>
                <strong style={{ fontSize: 15, color: '#2563eb' }}>{formatMoeda(estadoNova.compra.total)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 13, marginBottom: 2 }}>
                <strong style={{ color: 'var(--page-fg)' }}>Credito</strong>
                <strong style={{ fontSize: 15, color: '#16a34a' }}>{formatMoeda(estadoNova.compra.credito)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 13 }}>
                <strong style={{ color: 'var(--page-fg)' }}>Sobra</strong>
                <strong style={{ fontSize: 17, color: 'var(--page-fg)' }}>
                  {estadoNova.compra.credito >= estadoNova.compra.total
                    ? formatMoeda(estadoNova.compra.credito - estadoNova.compra.total)
                    : `falta ${formatMoeda(estadoNova.compra.total - estadoNova.compra.credito)}`}
                </strong>
              </div>
            </div>
            ) : (
            <div style={{
              minWidth: 240,
              background: '#fff',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '8px 12px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 13, marginBottom: 2 }}>
                <strong style={{ color: 'var(--page-fg)' }}>Devolvido</strong>
                <strong style={{ fontSize: 15, color: '#16a34a' }}>{formatMoeda(estadoNova.valorDevolvido)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 13, marginBottom: 2 }}>
                <strong style={{ color: 'var(--page-fg)' }}>Levado</strong>
                <strong style={{ fontSize: 15, color: '#2563eb' }}>{formatMoeda(estadoNova.valorLevado)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 13 }}>
                <strong style={{ color: 'var(--page-fg)' }}>Saldo</strong>
                <strong style={{
                  fontSize: 17,
                  color: estadoNova.saldoCredito > 0 ? '#16a34a' : estadoNova.saldoCredito < 0 ? '#dc2626' : 'var(--text-muted)',
                }}>
                  {estadoNova.saldoCredito >= 0 ? '+' : ''}{formatMoeda(estadoNova.saldoCredito)}
                </strong>
              </div>
            </div>
            )}
          </div>
        )}
      </div>

      {aba === 'nova' && (
        <div style={{ flex: 1, minHeight: 0 }}>
          <NovaTroca
            key={editarId ?? 'novo'}
            ref={novaTrocaRef}
            editarId={editarId}
            onConcluida={() => trocarAba('historico')}
            onEstadoChange={setEstadoNova}
          />
        </div>
      )}
      {aba === 'historico' && (
        <HistoricoTrocas
          onIrParaCreditos={() => trocarAba('creditos')}
          onCorrigir={irParaCorrigir}
        />
      )}
      {aba === 'creditos' && <CreditosLista />}
    </div>
  );
}
